import { IngestedFileRecord, ScannedFile } from '../types/domain';

/**
 * Normalizes a file path by replacing backslashes and trimming leading/trailing slashes.
 */
export function normalizePath(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
}

/**
 * Extracts category and sub-tags from a relative file path.
 *
 * @param relativePath The file's relative path (e.g. "Madeira/Con me/photo.jpg" or "Con me/photo.jpg")
 * @param isParentContainerDropped Whether the top segment is a root trip container folder
 */
export function extractCategoryFromPath(
  relativePath: string,
  isParentContainerDropped = false
): { category: string; subTags: string[] } {
  const normalized = normalizePath(relativePath);
  const segments = normalized.split('/').filter(Boolean);

  if (segments.length <= 1) {
    // Root photo with no enclosing folder
    return { category: 'Generale', subTags: [] };
  }

  if (isParentContainerDropped) {
    // Top segment is the root trip folder (e.g. "Madeira 2024")
    if (segments.length === 2) {
      // Photo directly inside the root container (e.g. "Madeira 2024/DSC_0001.JPG")
      return { category: 'Generale', subTags: [] };
    }
    const category = segments[1];
    const subTags = segments.slice(2, segments.length - 1);
    return { category, subTags };
  }

  // Direct category folder dropped (e.g. "Con me/DSC_0001.JPG")
  const category = segments[0];
  const subTags = segments.slice(1, segments.length - 1);
  return { category, subTags };
}

/**
 * Assigns categories and sub-tags to a collection of ingested files based on their relative paths.
 * Automatically detects whether the user dropped a single root trip folder, multiple category folders,
 * or loose photos.
 */
export function assignCategoriesToFiles(
  files: { file: File; relativePath: string }[]
): IngestedFileRecord[] {
  if (files.length === 0) return [];

  // 1. Normalize paths
  const normalized = files.map((item) => ({
    file: item.file,
    path: normalizePath(item.relativePath),
  }));

  // 2. Discover top-level root segments across the batch
  const topSegments = new Set<string>();
  let hasRootLevelFiles = false;

  for (const item of normalized) {
    const segments = item.path.split('/').filter(Boolean);
    if (segments.length <= 1) {
      hasRootLevelFiles = true;
    } else {
      topSegments.add(segments[0]);
    }
  }

  // 3. Detect if all files are under a single common root directory
  const isSingleCommonRoot = topSegments.size === 1 && !hasRootLevelFiles;
  const commonRoot = isSingleCommonRoot ? Array.from(topSegments)[0] : null;

  // 4. If under a single root, check if subdirectories exist inside that root
  let hasSubDirectoriesUnderRoot = false;
  if (isSingleCommonRoot && commonRoot) {
    for (const item of normalized) {
      const segments = item.path.split('/').filter(Boolean);
      if (segments.length > 2) {
        hasSubDirectoriesUnderRoot = true;
        break;
      }
    }
  }

  // 5. Map each file to its resolved category and sub-tags
  return normalized.map((item) => {
    const segments = item.path.split('/').filter(Boolean);

    // Case A: Loose file at root (e.g. "photo.jpg")
    if (segments.length <= 1) {
      return {
        file: item.file,
        relativePath: item.path,
        category: 'Generale',
        subTags: [],
      };
    }

    // Case B: Single root container folder dropped/selected
    if (isSingleCommonRoot && commonRoot) {
      if (hasSubDirectoriesUnderRoot) {
        // e.g. "Viaggio 2024/Con me/photo.jpg" -> "Con me"
        // e.g. "Viaggio 2024/photo.jpg" -> "Generale"
        if (segments.length === 2) {
          return {
            file: item.file,
            relativePath: item.path,
            category: 'Generale',
            subTags: [],
          };
        } else {
          const category = segments[1];
          const subTags = segments.slice(2, segments.length - 1);
          return {
            file: item.file,
            relativePath: item.path,
            category,
            subTags,
          };
        }
      } else {
        // User dropped a single category folder e.g. "Foto con me/p1.jpg"
        return {
          file: item.file,
          relativePath: item.path,
          category: commonRoot,
          subTags: [],
        };
      }
    }

    // Case C: Multiple folders dropped simultaneously (e.g. "Con me/p1.jpg", "Panorami/p2.jpg")
    const category = segments[0];
    const subTags = segments.slice(1, segments.length - 1);

    return {
      file: item.file,
      relativePath: item.path,
      category,
      subTags,
    };
  });
}

/**
 * Reads all entries from a FileSystemDirectoryReader, looping through 100-item
 * batches until the reader returns an empty array (resolving Chromium 100-item cap).
 */
export async function readAllEntriesFromDirectory(
  dirReader: FileSystemDirectoryReader
): Promise<FileSystemEntry[]> {
  const allEntries: FileSystemEntry[] = [];

  const readBatch = (): Promise<FileSystemEntry[]> => {
    return new Promise((resolve, reject) => {
      dirReader.readEntries(
        (results) => resolve(results),
        (error) => reject(error)
      );
    });
  };

  let batch: FileSystemEntry[] = await readBatch();
  while (batch.length > 0) {
    allEntries.push(...batch);
    batch = await readBatch();
  }

  return allEntries;
}

/**
 * Reads all directory entries given either a FileSystemDirectoryEntry or FileSystemDirectoryReader.
 */
export async function readAllDirectoryEntries(
  entryOrReader: FileSystemDirectoryEntry | FileSystemDirectoryReader
): Promise<FileSystemEntry[]> {
  const reader: FileSystemDirectoryReader =
    typeof (entryOrReader as FileSystemDirectoryEntry).createReader === 'function'
      ? (entryOrReader as FileSystemDirectoryEntry).createReader()
      : (entryOrReader as FileSystemDirectoryReader);

  return readAllEntriesFromDirectory(reader);
}

/**
 * Extracts the native File object from a FileSystemFileEntry.
 */
export function getFileFromEntry(fileEntry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    fileEntry.file(
      (file) => resolve(file),
      (error) => reject(error)
    );
  });
}

/**
 * Recursively traverses a FileSystemEntry, returning all files with relative paths.
 */
export async function traverseFileSystemEntry(
  entry: FileSystemEntry,
  currentPath: string = '',
  onProgress?: (scannedCount: number, currentDir: string) => void
): Promise<ScannedFile[]> {
  const results: ScannedFile[] = [];

  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    const file = await getFileFromEntry(fileEntry);
    const relativePath = currentPath ? `${currentPath}/${entry.name}` : entry.name;
    results.push({ file, relativePath });
    onProgress?.(results.length, currentPath || 'root');
  } else if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const dirReader = dirEntry.createReader();
    const childEntries = await readAllEntriesFromDirectory(dirReader);
    const childPath = currentPath ? `${currentPath}/${entry.name}` : entry.name;

    onProgress?.(results.length, childPath);

    for (const child of childEntries) {
      const childResults = await traverseFileSystemEntry(child, childPath, onProgress);
      results.push(...childResults);
      onProgress?.(results.length, childPath);
    }
  }

  return results;
}
